import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { 
  Shield, 
  Sun, 
  Moon, 
  LogOut, 
  Users, 
  Package,
  BarChart3,
  Search,
  ChevronLeft,
  ChevronRight,
  UserX,
  UserCheck,
  Trash2,
  Crown,
  Loader2,
  RefreshCw,
  DollarSign,
  TrendingUp,
  Bell,
  BookOpen
} from 'lucide-react';
import { formatCurrency, getStatusConfig } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboard() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [acquisitions, setAcquisitions] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [acquisitionsTotal, setAcquisitionsTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [acquisitionsPage, setAcquisitionsPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);

  // Check admin auth
  useEffect(() => {
    const adminData = localStorage.getItem('admin_data');
    const adminToken = localStorage.getItem('admin_token');
    
    if (!adminData || !adminToken) {
      navigate('/admin');
      return;
    }
    
    setAdmin(JSON.parse(adminData));
  }, [navigate]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: usersPage.toString(),
        limit: '10'
      });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setUsersTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [usersPage, searchQuery]);

  // Fetch acquisitions
  const fetchAcquisitions = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: acquisitionsPage.toString(),
        limit: '10'
      });
      
      const response = await fetch(`${API_URL}/api/admin/acquisitions?${params}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAcquisitions(data.acquisitions);
        setAcquisitionsTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch acquisitions:', error);
    }
  }, [acquisitionsPage]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAnalytics(), fetchUsers(), fetchAcquisitions()]);
      setLoading(false);
    };
    
    if (admin) {
      loadData();
    }
  }, [admin, fetchAnalytics, fetchUsers, fetchAcquisitions]);

  // Handlers
  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    navigate('/admin');
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status?is_active=${!currentStatus}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        toast.success(currentStatus ? 'User disabled' : 'User enabled');
        fetchUsers();
        fetchAnalytics();
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        toast.success('User deleted');
        setShowDeleteDialog(false);
        setSelectedUser(null);
        fetchUsers();
        fetchAnalytics();
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const updateSubscription = async (tier, days) => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users/${selectedUser.user_id}/subscription?tier=${tier}&expires_days=${days}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      
      if (response.ok) {
        toast.success('Subscription updated');
        setShowSubscriptionDialog(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  if (loading || !admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-7 w-7 text-gold" />
            <div>
              <span className="font-heading text-lg font-bold">Admin Portal</span>
              <Badge variant="outline" className="ml-2 text-xs">{admin.role}</Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">{admin.email}</span>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="admin-logout">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Analytics Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Total Users
              </div>
              <div className="text-2xl font-bold font-mono">{analytics?.users?.total || 0}</div>
              <div className="text-xs text-muted-foreground">
                {analytics?.users?.recent_signups || 0} this week
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Package className="h-4 w-4" />
                Acquisitions
              </div>
              <div className="text-2xl font-bold font-mono">{analytics?.acquisitions?.total || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                Total Cost
              </div>
              <div className="text-2xl font-bold font-mono">
                {formatCurrency(analytics?.acquisitions?.total_cost || 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                Expected Profit
              </div>
              <div className="text-2xl font-bold font-mono text-profit">
                {formatCurrency(analytics?.acquisitions?.total_expected_profit || 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <BookOpen className="h-4 w-4" />
                Total Books
              </div>
              <div className="text-2xl font-bold font-mono">{analytics?.books?.total || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="acquisitions" className="gap-2">
              <Package className="h-4 w-4" />
              Acquisitions
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">User Management</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users..."
                        className="pl-10 w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                      />
                    </div>
                    <Button variant="outline" size="icon" onClick={fetchUsers}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">User</th>
                        <th className="text-left py-2 font-medium">Status</th>
                        <th className="text-left py-2 font-medium">Subscription</th>
                        <th className="text-right py-2 font-medium">Acquisitions</th>
                        <th className="text-right py-2 font-medium">Est. Profit</th>
                        <th className="text-left py-2 font-medium">Joined</th>
                        <th className="text-center py-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.user_id} className="border-b hover:bg-muted/50">
                          <td className="py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center">
                                {user.picture ? (
                                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
                                ) : (
                                  <span className="text-sm font-medium text-gold">
                                    {user.name?.charAt(0) || 'U'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="font-medium">{user.name}</div>
                                <div className="text-xs text-muted-foreground">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3">
                            <Badge variant={user.is_active !== false ? "default" : "destructive"}>
                              {user.is_active !== false ? 'Active' : 'Disabled'}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <Badge variant="outline" className="gap-1">
                              {user.subscription_tier === 'pro' && <Crown className="h-3 w-3" />}
                              {user.subscription_tier || 'free'}
                            </Badge>
                          </td>
                          <td className="py-3 text-right font-mono">{user.acquisition_count || 0}</td>
                          <td className="py-3 text-right font-mono text-profit">
                            {formatCurrency(user.total_expected_profit || 0)}
                          </td>
                          <td className="py-3 text-muted-foreground">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => toggleUserStatus(user.user_id, user.is_active !== false)}
                                title={user.is_active !== false ? 'Disable User' : 'Enable User'}
                              >
                                {user.is_active !== false ? (
                                  <UserX className="h-4 w-4 text-destructive" />
                                ) : (
                                  <UserCheck className="h-4 w-4 text-profit" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowSubscriptionDialog(true);
                                }}
                                title="Manage Subscription"
                              >
                                <Crown className="h-4 w-4 text-gold" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowDeleteDialog(true);
                                }}
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {users.length} of {usersTotal} users
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage(p => p + 1)}
                      disabled={users.length < 10}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Acquisitions Tab */}
          <TabsContent value="acquisitions">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">All Acquisitions</CardTitle>
                  <Button variant="outline" size="icon" onClick={fetchAcquisitions}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Book</th>
                        <th className="text-left py-2 font-medium">User</th>
                        <th className="text-left py-2 font-medium">Supplier</th>
                        <th className="text-right py-2 font-medium">Cost</th>
                        <th className="text-right py-2 font-medium">Profit</th>
                        <th className="text-center py-2 font-medium">Status</th>
                        <th className="text-left py-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acquisitions.map((acq) => {
                        const statusConfig = getStatusConfig(acq.status);
                        return (
                          <tr key={acq.acquisition_id} className="border-b hover:bg-muted/50">
                            <td className="py-3">
                              <div className="font-medium">{acq.book?.title || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground">{acq.book?.isbn}</div>
                            </td>
                            <td className="py-3">
                              <div className="text-sm">{acq.user?.name || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground">{acq.user?.email}</div>
                            </td>
                            <td className="py-3 text-muted-foreground">{acq.supplier_name}</td>
                            <td className="py-3 text-right font-mono">{formatCurrency(acq.purchase_price)}</td>
                            <td className="py-3 text-right font-mono text-profit">
                              {formatCurrency(acq.expected_profit)}
                            </td>
                            <td className="py-3 text-center">
                              <Badge variant="outline" className={`text-xs ${statusConfig.className}`}>
                                {statusConfig.label}
                              </Badge>
                            </td>
                            <td className="py-3 text-muted-foreground">
                              {new Date(acq.purchase_date || acq.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {acquisitions.length} of {acquisitionsTotal} acquisitions
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAcquisitionsPage(p => Math.max(1, p - 1))}
                      disabled={acquisitionsPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAcquisitionsPage(p => p + 1)}
                      disabled={acquisitions.length < 10}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Subscription Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Subscription Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(analytics?.users?.subscription_breakdown || {}).map(([tier, count]) => (
                      <div key={tier} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {tier === 'pro' && <Crown className="h-4 w-4 text-gold" />}
                          <span className="capitalize">{tier || 'Free'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gold rounded-full"
                              style={{ width: `${(count / (analytics?.users?.total || 1)) * 100}%` }}
                            />
                          </div>
                          <span className="font-mono text-sm">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Acquisition Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Acquisition Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics?.acquisitions?.by_status || {}).map(([status, data]) => {
                      const config = getStatusConfig(status);
                      return (
                        <div key={status} className="flex items-center justify-between">
                          <Badge variant="outline" className={`${config.className}`}>
                            {config.label}
                          </Badge>
                          <div className="text-right">
                            <span className="font-mono">{data.count}</span>
                            <span className="text-muted-foreground text-xs ml-2">
                              ({formatCurrency(data.profit)})
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Shipment Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Shipment Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics?.shipments?.by_status || {}).map(([status, data]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="capitalize">{status}</span>
                        <div className="text-right">
                          <span className="font-mono">{data.count}</span>
                          <span className="text-muted-foreground text-xs ml-2">
                            ({data.units} units)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Active Users</span>
                      <span className="font-mono">{analytics?.users?.active || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Disabled Users</span>
                      <span className="font-mono">{analytics?.users?.disabled || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Active Alerts</span>
                      <span className="font-mono">{analytics?.alerts?.active || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Shipments</span>
                      <span className="font-mono">{analytics?.shipments?.total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Realized Profit</span>
                      <span className="font-mono text-profit">
                        {formatCurrency(analytics?.acquisitions?.realized_profit || 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This will permanently delete:
              <ul className="list-disc ml-6 mt-2">
                <li>User account</li>
                <li>All acquisitions</li>
                <li>All shipments</li>
                <li>All alerts</li>
              </ul>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteUser}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Subscription</DialogTitle>
            <DialogDescription>
              Update subscription for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-3">
              <Button 
                variant="outline" 
                className="flex flex-col h-auto py-4"
                onClick={() => updateSubscription('free', 0)}
              >
                <span className="font-medium">Free</span>
                <span className="text-xs text-muted-foreground">Basic features</span>
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col h-auto py-4 border-gold"
                onClick={() => updateSubscription('pro', 30)}
              >
                <Crown className="h-4 w-4 text-gold mb-1" />
                <span className="font-medium">Pro</span>
                <span className="text-xs text-muted-foreground">30 days</span>
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col h-auto py-4 border-gold"
                onClick={() => updateSubscription('pro', 365)}
              >
                <Crown className="h-4 w-4 text-gold mb-1" />
                <span className="font-medium">Pro</span>
                <span className="text-xs text-muted-foreground">1 year</span>
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
